import CardAboutModal from "@/components/CardAboutModal";
import CardDelisted from "@/components/CardDelisted";
import DiscoverIconSM from "@/components/DiscoverIconSm";
import PlayBox from "@/components/PlayBox";
import ThisIsModal from "@/components/ThisIsModal";
import Thumbnail from "@/components/Thumbnails";
import {
  Card,
  CardBody,
  Center,
  Container,
  Divider,
  HStack,
  Stack,
  Text,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { useInView } from "framer-motion";
import type { InferGetStaticPropsType } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRef } from "react";
import { z } from "zod";

export const ArtistProps = z.object({
  name: z.string().nullish(),
  title: z.string().nullish(),
  slug: z.string().nullish(),
  about: z.string().nullish(),
});

export const CardFiles = z.object({
  file_name: z.string().nullish(),
});

export const CardProps = z.object({
  card: z.object({
    id: z.number(),
    ext_file: z.string().nullish(),
    scid: z.string().nullish(),
    comp_file: z.string().nullish(),
    image: z.string().nullish(),
    price: z.number().nullish(),
    uuid: z.string(),
    title: z.string(),
    song: z.object({
      file: z.string().nullish(),
      ext_file: z.string().nullish(),
    }),

    issued: z.string().nullish(),
    primary_artist: ArtistProps.nullish(),
    files: z.array(CardFiles).nullish(),
    preview_length: z.bigint().nullable(),
  }),
});

export default function CardPage({
  card,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const { colorMode } = useColorMode();
  const backgroundColor = colorMode === "light" ? "white" : "black";
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      {card.uuid && card.delisted && (
        <>
          <CardDelisted />
        </>
      )}
      {!card.uuid && (
        <>
          <CardDelisted />
        </>
      )}
      {card.uuid && !card.delisted && (
        <div>
          <Head>
            <title>{`${card.primary_artist?.name ?? ""} - ${
              card.title
            } | Songcards`}</title>
            <meta property="og:site_name" content="Songcards" />
            <meta
              itemProp="image"
              content={`${card.comp_file ?? card.ext_file ?? ""}`}
            />
            <link
              rel="canonical"
              href={`https://songcards.io/cards/${card.uuid}`}
            />
            <meta name="description" content="Listen. Discover. Collect." />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <link rel="apple-touch-icon" href="/SC Icon_A_Light.svg" />
            <link rel="icon" href="/SC Icon_A_Light.svg" />

            <meta
              property="og:image"
              content={`${card.comp_file ?? card.ext_file ?? ""}`}
            />
            <meta
              property="og:url"
              content={`https://songcards.io/cards/${card.uuid}`}
            />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Songcards" />
            <meta
              property="og:title"
              content={`${card.primary_artist?.name ?? ""} - ${
                card.title
              } | Songcards`}
            />
            <meta
              property="og:description"
              content="Listen. Discover. Collect."
            />
            <meta name="twitter:domain" content="songcards.io" />
            <meta name="twitter:site" content="@unlockyoursound" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:title"
              content={`${card.primary_artist?.name ?? ""} - ${
                card.title
              } | Songcards`}
            />
            <meta
              name="twitter:description"
              content="Listen. Discover. Collect."
            />

            <meta
              name="twitter:image"
              content={`${card.comp_file ?? card.ext_file ?? ""}`}
            />
            <meta name="apple-itunes-app" content="app-id=6737206374" />
          </Head>
          <Center
            ref={ref}
            style={{
              transform: isInView ? "none" : "translateY(+100px)",
              transition: "all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s",
            }}
          >
            <Card
              maxW="xs"
              align="center"
              bgColor={colorMode === "dark" ? "black" : "white"}
              variant="outline"
              borderColor={colorMode === "dark" ? "white" : "black"}
              borderStyle="dotted"
              borderRadius={15}
              shadow={"md"}
            >
              <CardBody>
                <Thumbnail
                  ext_file={card.ext_file}
                  comp_file={card.comp_file}
                />
                <Center>
                  <Stack mt={5} mb="0" spacing="3">
                    <PlayBox card={card} collection={false} />
                    <Text ml={4} mb={1} mt={2}>
                      is a Songcard from
                      <br />
                      {card.primary_artist && (
                        <Text as="b" key={card.primary_artist.slug}>
                          <Link href={`/artists/${card.primary_artist.slug}`}>
                            {card.primary_artist.name}
                          </Link>
                        </Text>
                      )}
                    </Text>
                    <Divider mb={3} />
                    <Center>
                      <HStack justify="space-between" w="100%">
                        <CardAboutModal
                          card={card}
                          price={false}
                          showOpen={true}
                        />
                        <ThisIsModal
                          card={card}
                          price={false}
                          showOpen={false}
                        />
                      </HStack>
                    </Center>
                  </Stack>
                </Center>
              </CardBody>
            </Card>
          </Center>
          <DiscoverIconSM />
          <Container mb={200}>
            <></>
          </Container>
        </div>
      )}
    </>
  );
}

interface ParamProps {
  params: {
    uuid: string;
  };
}

export async function getStaticProps({ params }: ParamProps) {
  const { uuid } = params;
  const apiUrl = process.env.NEXT_PUBLIC_BASE_API_URL;
  const res = await fetch(`${apiUrl}/cards/${uuid}/`);
  const card = await res.json();
  console.log(card.preview_length);

  return {
    props: {
      card,
    },
    revalidate: 604800,
  };
}

export async function getStaticPaths() {
  const apiUrl = process.env.NEXT_PUBLIC_BASE_API_URL;
  const endpoint = `${apiUrl}/cards/`;

  if (endpoint) {
    const response = await fetch(endpoint);
    const cards = await response.json();

    const paths = await cards.map(
      (card: z.infer<typeof CardProps>["card"]) => ({
        params: { uuid: card.uuid },
      }),
    );

    return { paths, fallback: "blocking" };
  }
}
